// plugins/axios.ts
import axios, { AxiosError, type AxiosInstance } from 'axios'
import type { Router } from 'vue-router'
import type { AxiosRequestConfig } from 'axios'
import { loadingStart, loadingStop } from '@/composables/useLoading'

export const api = axios.create({
  baseURL: window.location.href.indexOf('localhost') > -1? CONFIG.DEV_API_BASE_URL: CONFIG.API_BASE_URL,
  timeout: 360000,
}) as AxiosInstance & { isAxiosError: (e: unknown) => e is AxiosError }

api.isAxiosError = (e: unknown): e is AxiosError => axios.isAxiosError(e)

api.interceptors.request.use((config) => {
  loadingStart()
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>).Authorization = `Bearer ${token}`
  }
  return config
})

let wired = false
export function wireAuthRedirect(router: Router) {
  if (wired) return
  let redirecting = false

api.interceptors.response.use(
  (res) => {
    loadingStop()
    return res
  },
  async (err: AxiosError) => {
    loadingStop()
    const status = err.response?.status
    const original = err.config as (AxiosRequestConfig & { _retried?: boolean })


    const { useAuth } = await import('@/stores/auth')
    const auth = useAuth()


    const justSet = Date.now() - auth.lastTokenSetAt < 1000
    const hasAuthHeader = !!original?.headers?.Authorization

    if (status === 401 && justSet && hasAuthHeader && !original._retried) {
      original._retried = true

      return api(original)
    }

    if (status === 401 && !redirecting) {
      redirecting = true
      localStorage.removeItem('auth_token')
      auth.$patch({ isLoggedIn: false, user: null })

      const current = router.currentRoute.value.fullPath
      const target = current.includes('redirect=') ? '/' : `/?redirect=${encodeURIComponent(current)}`
      try {
        await router.isReady()
        if (router.currentRoute.value.fullPath !== target) await router.replace(target)
      } catch { window.location.href = target }
      finally { setTimeout(() => { redirecting = false }, 350) }
    }

    return Promise.reject(err)
  }
)

  wired = true
}
